import type { JourneyPage } from './types'

export const portal: JourneyPage = {
  slug: 'portal',
  intro: [
    { text: '89% of advisers have a client ', highlight: 'portal' },
    { text: 'Engaged clients trust their adviser more. ', highlight: '70% more' },
    { text: 'But engagement requires ', highlight: 'content' },
    { text: 'Only 18% of clients find their portal ', highlight: 'useful' },
    { text: 'Ours auto-populates. DocuSign built in. ', highlight: 'Your brand. Free' },
  ],
  colorClass: 'portal',
  seo: {
    title: 'WealthSpace Client Portal | DocuSign & Document Vault',
    description:
      'Branded client portal with DocuSign integration, secure document vault, and 24/7 client access. Transform annual reviews into always-on engagement.',
    keywords:
      'client portal, DocuSign, document management, client engagement, secure vault, adviser portal, client access, wealth dashboard, e-signatures',
  },
  hero: {
    eyebrow: 'The WealthSpace Portal',
    title: 'From annual review to',
    titleAccent: 'always engaged',
    subtitle:
      "Client portals aren't new. But a portal that's automatically populated with AI-generated documents, complete audit trails, DocuSign integration, and live Value of Advice? That changes everything.",
    microcopy: 'Branded to your firm. Ready in minutes.',
  },
  journeySection: {
    label: 'The Transformation',
    title: 'Your clients,',
    titleAccent: 'always connected',
    subtitle: 'See how the Portal changes the adviser-client relationship.',
  },
  timeline: [
    {
      time: 'The Old Way',
      icon: 'clock',
      iconColor: 'amber',
      title: 'Once a year',
      meta: 'Annual review meeting',
      body: "Mrs Patterson sees you once a year. Between meetings, she wonders about her pension value. She digs through emails for documents. She waits for your callback about that ISA question.",
      previewLabel: 'The result',
      previewText:
        'Low engagement. Anxious clients. Missed opportunities to demonstrate value.',
    },
    {
      time: 'Day One',
      icon: 'users',
      iconColor: 'indigo',
      title: 'Portal invitation sent',
      meta: 'Branded to your firm',
      body: "You invite Mrs Patterson to her personal portal. It carries your firm's branding, your colours, your logo. She creates an account in under a minute - and her documents are already waiting.",
      previewLabel: 'The difference',
      previewText:
        'Every suitability report you generate in WealthSpace automatically appears in her portal. No uploading. No emailing. It just works.',
    },
    {
      time: 'Ongoing',
      icon: 'document',
      iconColor: 'teal',
      title: 'Complete audit trail',
      meta: 'Every action logged',
      body: 'Every document viewed, every signature completed, every message sent - automatically logged with timestamps. When the FCA asks questions, you have answers. When clients need proof, it\'s there.',
      previewLabel: 'For compliance',
      previewText:
        'Full audit history: Document sent 14:32. Viewed 16:45. Signed 17:12. Archived automatically. Sleep easier.',
    },
    {
      time: 'When needed',
      icon: 'document',
      iconColor: 'violet',
      title: 'DocuSign built in',
      meta: 'Industry-standard e-signatures',
      body: "When you need a signature, the client gets a DocuSign notification right in their portal. They review, sign, and it's done - with a full audit trail automatically logged. No printing. No posting. No chasing.",
      previewLabel: 'Average time to signature',
      previewText:
        'Under 4 hours vs. 5-7 days with post. Every signature timestamped, every document archived automatically.',
    },
    {
      time: 'Always visible',
      icon: 'chart',
      iconColor: 'emerald',
      title: 'Value of Advice',
      meta: 'Show, don\'t tell',
      body: "Every time they log in, clients see their personalised Value of Advice summary. The tax saved. The pension optimised. The peace of mind delivered. Your worth, quantified and visible.",
      previewLabel: 'What clients see',
      previewText:
        '"Your advice has delivered £34,200 in value this year" - Retention improves. Referrals increase. Fees are justified.',
    },
  ],
  result: {
    title: 'From annual to',
    titleAccent: 'always',
    body: 'The Portal transforms your client relationship from a once-a-year event into an ongoing conversation. Clients feel connected. You feel valued. Everyone wins.',
    stats: [
      { value: '24/7', label: 'Client access to their wealth' },
      { value: '70%', label: 'Increase in client trust with active portal use' },
      { value: '3x', label: 'More client touchpoints' },
    ],
  },
  features: {
    label: 'Portal Features',
    title: 'Everything they need,',
    titleAccent: 'one place',
    subtitle:
      'The Portal gives your clients a premium experience that reflects the quality of your advice.',
    items: [
      {
        icon: 'chart',
        title: 'Wealth dashboard',
        body: 'Clients see their total wealth at a glance - pensions, ISAs, investments, property. Updated regularly with portfolio valuations from major platforms.',
      },
      {
        icon: 'document',
        title: 'Auto-populated vault',
        body: 'When WealthSpace generates a suitability report, it appears in the client\'s portal automatically. No uploading. No emailing attachments. Documents flow seamlessly from advice to client.',
      },
      {
        icon: 'users',
        title: 'Secure messaging',
        body: 'A simple, secure way for clients to ask questions without picking up the phone. You respond when it suits you. Conversations are logged for compliance.',
      },
    ],
  },
  testimonial: {
    quote:
      "89% of advisers have a client portal, but only 18% of clients find them useful. We built ours differently - it auto-populates with your AI-generated documents, has DocuSign built in, and shows clients exactly what your advice is worth.",
    authorName: 'The WealthSpace Team',
    authorRole: 'Built by Advisers, for Advisers',
    initials: 'WS',
  },
  cta: {
    title: 'Give your clients a',
    titleAccent: 'premium experience',
    subtitle:
      'The Portal is included with WealthSpace. See how it transforms your client relationships - book a demo today.',
    note: 'Branded to your firm. Set up in minutes. Clients love it.',
  },
}
